import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { useCRMAuth } from '@/contexts/CRMAuthContext';
import CRMLayout from './CRMLayout';
import { toast } from 'sonner';
import {
  Bell, Mail, Clock, CheckCircle2, AlertTriangle, 
  Calendar, Loader2, Save, Download, FileText, Users, ClipboardList, Archive,
  MessageCircle, Send, Phone
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

export default function CRMSettings() {
  const { token, user, isAdmin } = useCRMAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(null);
  const [testingWA, setTestingWA] = useState(false);
  const [settings, setSettings] = useState({
    email_task_assigned: true,
    email_task_completed: true,
    email_deadline_reminder: true,
    email_daily_summary: true,
    email_overdue_alerts: true
  });
  const [waSettings, setWaSettings] = useState({
    enabled: true,
    admin_phone: '',
    notify_task_assigned: true,
    notify_task_completed: true,
    notify_deadlines: true,
    notify_leave_requests: true,
    notify_daily_summary: false
  });

  useEffect(() => { fetchSettings(); fetchWASettings(); }, []);

  const fetchSettings = async () => {
    try {
      const res = await fetch(`${API}/api/crm/settings/notifications`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        if (data.settings) setSettings(data.settings);
      }
    } catch (err) {}
    finally { setLoading(false); }
  };

  const fetchWASettings = async () => {
    try {
      const res = await fetch(`${API}/api/crm/whatsapp/settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setWaSettings(data);
      }
    } catch (err) {}
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Save email settings
      await fetch(`${API}/api/crm/settings/notifications`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(settings)
      });
      // Save WhatsApp settings
      await fetch(`${API}/api/crm/whatsapp/settings`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(waSettings)
      });
      toast.success('Settings saved!');
    } catch (err) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const testWhatsApp = async () => {
    if (!waSettings.admin_phone) {
      toast.error('Enter admin phone number');
      return;
    }
    setTestingWA(true);
    try {
      const res = await fetch(`${API}/api/crm/whatsapp/test`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: waSettings.admin_phone, type: 'test' })
      });
      if (res.ok) {
        toast.success('Test message sent! Check WhatsApp.');
      } else {
        toast.error('Failed to send test message');
      }
    } catch (err) {
      toast.error('Failed');
    } finally {
      setTestingWA(false);
    }
  };

  const toggleSetting = (key) => {
    setSettings(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleExport = async (type) => {
    setExporting(type);
    try {
      const endpoint = type === 'all' ? '/api/crm/export/all' : `/api/crm/export/${type}`;
      const res = await fetch(`${API}${endpoint}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = type === 'all' ? `crm_backup_${new Date().toISOString().slice(0,10)}.zip` : `${type}_${new Date().toISOString().slice(0,10)}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        toast.success(`${type === 'all' ? 'Full backup' : type.charAt(0).toUpperCase() + type.slice(1)} exported!`);
      } else {
        toast.error('Export failed');
      }
    } catch (err) {
      toast.error('Export failed');
    } finally {
      setExporting(null);
    }
  };

  if (loading) {
    return (
      <CRMLayout>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
        </div>
      </CRMLayout>
    );
  }

  return (
    <CRMLayout>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Settings</h1>
          <p className="text-slate-500">Manage your notification preferences</p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Save Changes
        </Button>
      </div>

      <Card className="p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
            <Bell className="w-5 h-5 text-indigo-600" />
          </div>
          <div>
            <h2 className="font-semibold text-slate-900">Email Notifications</h2>
            <p className="text-sm text-slate-500">Choose what emails you want to receive</p>
          </div>
        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
            <div className="flex items-center gap-3">
              <Mail className="w-5 h-5 text-blue-500" />
              <div>
                <p className="font-medium text-slate-900">Task Assigned</p>
                <p className="text-sm text-slate-500">Get notified when a task is assigned to you</p>
              </div>
            </div>
            <Switch checked={settings.email_task_assigned} onCheckedChange={() => toggleSetting('email_task_assigned')} />
          </div>

          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="w-5 h-5 text-emerald-500" />
              <div>
                <p className="font-medium text-slate-900">Task Completed</p>
                <p className="text-sm text-slate-500">Get notified when tasks are completed (admins only)</p>
              </div>
            </div>
            <Switch checked={settings.email_task_completed} onCheckedChange={() => toggleSetting('email_task_completed')} />
          </div>

          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
            <div className="flex items-center gap-3">
              <Clock className="w-5 h-5 text-amber-500" />
              <div>
                <p className="font-medium text-slate-900">Deadline Reminders</p>
                <p className="text-sm text-slate-500">Get reminded about upcoming deadlines</p>
              </div>
            </div>
            <Switch checked={settings.email_deadline_reminder} onCheckedChange={() => toggleSetting('email_deadline_reminder')} />
          </div>

          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
            <div className="flex items-center gap-3">
              <Calendar className="w-5 h-5 text-purple-500" />
              <div>
                <p className="font-medium text-slate-900">Daily Summary</p>
                <p className="text-sm text-slate-500">Receive a daily summary of your tasks at 9 AM</p>
              </div>
            </div>
            <Switch checked={settings.email_daily_summary} onCheckedChange={() => toggleSetting('email_daily_summary')} />
          </div>

          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              <div>
                <p className="font-medium text-slate-900">Overdue Alerts</p>
                <p className="text-sm text-slate-500">Get alerted about overdue tasks</p>
              </div>
            </div>
            <Switch checked={settings.email_overdue_alerts} onCheckedChange={() => toggleSetting('email_overdue_alerts')} />
          </div>
        </div>
      </Card>

      <Card className="p-6 mt-6">
        <h3 className="font-semibold text-slate-900 mb-4">Email Preview</h3>
        <p className="text-sm text-slate-500 mb-4">Emails will be sent to: <strong>{user?.email}</strong></p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="p-4 border rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-2 h-2 rounded-full bg-blue-500" />
              <span className="text-sm font-medium">Task Assignment</span>
            </div>
            <p className="text-xs text-slate-500">Sent immediately when assigned</p>
          </div>
          <div className="p-4 border rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-2 h-2 rounded-full bg-amber-500" />
              <span className="text-sm font-medium">Deadline Reminder</span>
            </div>
            <p className="text-xs text-slate-500">Sent every 8 hours for due tasks</p>
          </div>
          <div className="p-4 border rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-2 h-2 rounded-full bg-purple-500" />
              <span className="text-sm font-medium">Daily Summary</span>
            </div>
            <p className="text-xs text-slate-500">Sent daily at 9:00 AM</p>
          </div>
          <div className="p-4 border rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-2 h-2 rounded-full bg-emerald-500" />
              <span className="text-sm font-medium">Task Completed</span>
            </div>
            <p className="text-xs text-slate-500">Sent to admins on completion</p>
          </div>
        </div>
      </Card>

      {/* Data Export Section - Admin Only */}
      {isAdmin() && (
        <Card className="p-6 mt-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
              <Download className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <h2 className="font-semibold text-slate-900">Data Export & Backup</h2>
              <p className="text-sm text-slate-500">Download your CRM data as CSV files</p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Button 
              variant="outline" 
              className="h-auto py-4 flex flex-col items-center gap-2"
              onClick={() => handleExport('clients')}
              disabled={exporting === 'clients'}
            >
              <Users className={`w-6 h-6 text-blue-500 ${exporting === 'clients' ? 'animate-pulse' : ''}`} />
              <span className="font-medium">Export Clients</span>
              <span className="text-xs text-slate-500">Download as CSV</span>
            </Button>

            <Button 
              variant="outline" 
              className="h-auto py-4 flex flex-col items-center gap-2"
              onClick={() => handleExport('tasks')}
              disabled={exporting === 'tasks'}
            >
              <ClipboardList className={`w-6 h-6 text-purple-500 ${exporting === 'tasks' ? 'animate-pulse' : ''}`} />
              <span className="font-medium">Export Tasks</span>
              <span className="text-xs text-slate-500">Download as CSV</span>
            </Button>

            <Button 
              variant="outline" 
              className="h-auto py-4 flex flex-col items-center gap-2"
              onClick={() => handleExport('work-logs')}
              disabled={exporting === 'work-logs'}
            >
              <FileText className={`w-6 h-6 text-amber-500 ${exporting === 'work-logs' ? 'animate-pulse' : ''}`} />
              <span className="font-medium">Export Work Logs</span>
              <span className="text-xs text-slate-500">Download as CSV</span>
            </Button>

            <Button 
              variant="outline" 
              className="h-auto py-4 flex flex-col items-center gap-2 border-emerald-200 bg-emerald-50 hover:bg-emerald-100"
              onClick={() => handleExport('all')}
              disabled={exporting === 'all'}
            >
              <Archive className={`w-6 h-6 text-emerald-600 ${exporting === 'all' ? 'animate-pulse' : ''}`} />
              <span className="font-medium text-emerald-700">Full Backup</span>
              <span className="text-xs text-emerald-600">Download as ZIP</span>
            </Button>
          </div>
        </Card>
      )}

      {/* WhatsApp Notifications - Admin Only */}
      {isAdmin() && (
        <Card className="p-6 mt-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <MessageCircle className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <h2 className="font-semibold text-slate-900">WhatsApp Notifications</h2>
              <p className="text-sm text-slate-500">Receive important alerts on WhatsApp</p>
            </div>
          </div>

          {/* Admin Phone */}
          <div className="mb-6">
            <label className="text-sm font-medium mb-2 block">Admin Phone Number</label>
            <div className="flex gap-2">
              <div className="flex items-center gap-2 px-3 bg-slate-100 rounded-l-lg border border-r-0">
                <Phone className="w-4 h-4 text-slate-500" />
                <span className="text-sm text-slate-500">+91</span>
              </div>
              <Input 
                value={waSettings.admin_phone?.replace(/^91/, '')}
                onChange={(e) => setWaSettings({...waSettings, admin_phone: '91' + e.target.value.replace(/\D/g, '')})}
                placeholder="8269911081"
                className="rounded-l-none flex-1"
              />
              <Button 
                variant="outline" 
                onClick={testWhatsApp}
                disabled={testingWA}
              >
                {testingWA ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                <span className="ml-2">Test</span>
              </Button>
            </div>
          </div>

          {/* Notification Toggles */}
          <div className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div className="flex items-center gap-3">
                <CheckCircle2 className="w-5 h-5 text-green-500" />
                <div>
                  <p className="font-medium text-slate-900">Task Assigned</p>
                  <p className="text-sm text-slate-500">When a task is assigned to someone</p>
                </div>
              </div>
              <Switch 
                checked={waSettings.notify_task_assigned}
                onCheckedChange={(v) => setWaSettings({...waSettings, notify_task_assigned: v})}
              />
            </div>

            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div className="flex items-center gap-3">
                <CheckCircle2 className="w-5 h-5 text-blue-500" />
                <div>
                  <p className="font-medium text-slate-900">Task Completed</p>
                  <p className="text-sm text-slate-500">When a task is marked complete</p>
                </div>
              </div>
              <Switch 
                checked={waSettings.notify_task_completed}
                onCheckedChange={(v) => setWaSettings({...waSettings, notify_task_completed: v})}
              />
            </div>

            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div className="flex items-center gap-3">
                <Clock className="w-5 h-5 text-amber-500" />
                <div>
                  <p className="font-medium text-slate-900">Deadline Reminders</p>
                  <p className="text-sm text-slate-500">Before task deadlines</p>
                </div>
              </div>
              <Switch 
                checked={waSettings.notify_deadlines}
                onCheckedChange={(v) => setWaSettings({...waSettings, notify_deadlines: v})}
              />
            </div>

            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div className="flex items-center gap-3">
                <Calendar className="w-5 h-5 text-purple-500" />
                <div>
                  <p className="font-medium text-slate-900">Leave Requests</p>
                  <p className="text-sm text-slate-500">When employees request leave</p>
                </div>
              </div>
              <Switch 
                checked={waSettings.notify_leave_requests}
                onCheckedChange={(v) => setWaSettings({...waSettings, notify_leave_requests: v})}
              />
            </div>
          </div>
        </Card>
      )}
    </CRMLayout>
  );
}
